import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useApi } from "@/lib/api";
import { AdminNav } from "@/components/admin/AdminNav";

interface FailedItem {
  id: string;
  title: string;
  url: string;
  source_name: string | null;
  ingested_at: number;
  last_error: string | null;
  last_attempt_at: number | null;
}

export function AdminSynthesis() {
  const api = useApi();
  const qc = useQueryClient();
  const list = useQuery({
    queryKey: ["admin-items-failed"],
    queryFn: () => api.get<{ items: FailedItem[] }>("/admin/items/failed"),
  });

  const retry = useMutation({
    mutationFn: (id: string) => api.post(`/admin/items/${id}/retry`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["admin-items-failed"] }),
  });

  const items = list.data?.items ?? [];

  return (
    <div className="px-10 py-10 max-w-6xl">
      <header className="mb-8">
        <p className="label-eyebrow">Operations</p>
        <h1 className="mt-2 text-display-2 font-display">Synthesis</h1>
        <p className="mt-3 text-whisper text-sm max-w-xl leading-relaxed">
          Items that failed AI synthesis. Retry returns them to the pending
          queue for the next consumer pass.
        </p>
      </header>

      <AdminNav />

      {list.isLoading ? (
        <p className="text-sm text-whisper">Reading failed items…</p>
      ) : items.length === 0 ? (
        <p className="text-sm text-whisper">Nothing requiring attention.</p>
      ) : (
        <div className="panel divide-y divide-veil/40">
          {items.map((it) => (
            <div
              key={it.id}
              className="grid grid-cols-[1fr_auto] items-start gap-6 px-6 py-4"
            >
              <div className="min-w-0">
                <div className="text-[10px] uppercase tracking-[0.18em] text-muted">
                  {it.source_name ?? "—"} ·{" "}
                  {new Date(it.ingested_at).toLocaleString(undefined, {
                    month: "short",
                    day: "numeric",
                    hour: "2-digit",
                    minute: "2-digit",
                  })}
                </div>
                <a
                  href={it.url}
                  target="_blank"
                  rel="noreferrer"
                  className="mt-1 font-display text-sm leading-snug hover:text-gold-glow transition-colors block truncate"
                >
                  {it.title}
                </a>
                {it.last_error && (
                  <p className="mt-2 text-xs text-gold-glow leading-relaxed line-clamp-2">
                    ⚠ {it.last_error}
                  </p>
                )}
              </div>
              <button
                type="button"
                onClick={() => retry.mutate(it.id)}
                disabled={retry.isPending}
                className="text-[10px] uppercase tracking-[0.18em] text-whisper hover:text-fog transition-colors disabled:opacity-40"
              >
                Retry synthesis
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
